import { Permissions } from '@zspace/roles';
import { DownloadableData, SelectionFilter } from '@zspace/types';
import { useCallback, useMemo } from 'react';
import { FaDownload } from 'react-icons/fa6';
import CheckPermissions from '../../../shared/check-permissions/check-permissions';
import { useAdminContext } from '../../../shared/context/admin-context';
import { useCSVDownload } from '../../../shared/hooks/csv-download';
import useHttpRequest from '../../../shared/hooks/http-request';
import Button from '../../../ui/button/button';
import { Tooltip } from '../../../ui/tooltip/tooltip';

const EXPORT_FAILED_ERROR_MESSAGE = 'Export failed. Please try again';

export type AdminHeaderExportProps = {
  onExport?: (inboxItemId: string) => Promise<DownloadableData<string>>;
  requiredPermissions: Permissions[];
};

export function AdminHeaderExport({
  onExport,
  requiredPermissions,
}: AdminHeaderExportProps) {
  const { selectedInboxItem, exportTooltipMessage } = useAdminContext();
  const {
    executeHttpRequest: executeExportRequest,
    isLoading: isLoadingExport,
  } = useHttpRequest();
  const { downloadCSV } = useCSVDownload();

  const selectedAllItems = useMemo(
    () => selectedInboxItem === SelectionFilter.ALL,
    [selectedInboxItem]
  );

  const handleExportClick = useCallback(
    () =>
      executeExportRequest({
        asyncFunction: async () => {
          if (onExport && selectedInboxItem && !selectedAllItems) {
            const response = await onExport(selectedInboxItem);
            downloadCSV(response);
          }
        },
        customErrorMessage: EXPORT_FAILED_ERROR_MESSAGE,
      }),
    [
      downloadCSV,
      executeExportRequest,
      onExport,
      selectedAllItems,
      selectedInboxItem,
    ]
  );

  const exportButton = useMemo(
    () => (
      <Button
        color="primary-dark"
        isExecutingAction={isLoadingExport}
        onClick={handleExportClick}
      >
        <Button.LoadingIcon icon={FaDownload} />
        <span>Export</span>
      </Button>
    ),
    [handleExportClick, isLoadingExport]
  );

  const exportButtonWithTooltip = useMemo(() => {
    if (!selectedInboxItem || selectedAllItems) {
      return (
        <Tooltip message={exportTooltipMessage} type="error">
          {exportButton}
        </Tooltip>
      );
    }

    return exportButton;
  }, [exportButton, exportTooltipMessage, selectedAllItems, selectedInboxItem]);

  const shouldNotRender = useMemo(
    () => !onExport || !exportTooltipMessage,
    [onExport, exportTooltipMessage]
  );

  if (shouldNotRender) {
    return null;
  }

  return (
    <CheckPermissions permissions={requiredPermissions}>
      <CheckPermissions.Render>
        {exportButtonWithTooltip}
      </CheckPermissions.Render>
    </CheckPermissions>
  );
}
